import type { PaginatedList, Session } from '@local/common-lib';
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { ConfirmButton } from '../../components/confirm-button/index.js';
import { RelativeDate } from '../../components/date/index.js';
import { FormatUserAgent } from './user-agent.js';

interface Props {
  revoke: (session: Session.Type) => unknown;
  revoking: boolean;
  sessions: PaginatedList<Session.Type>;
}

export const SessionList: FC<Props> = ({ revoke, revoking, sessions }) => {
  return (
    <Table aria-label="sessions">
      <TableBody>
        {sessions.items.map((row) => (
          <TableRow
            key={row._id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              <Stack direction="column" spacing={{ md: 0, xs: 1 }}>
                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={{ md: 1, xs: 0 }}
                >
                  <Typography>
                    <RelativeDate date={DateTime.fromJSDate(row.issued)} />
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Typography color="text.secondary">from</Typography>
                    <Typography>
                      {row.ips.length ? row.ips[0] : <i>unknown</i>}
                    </Typography>
                  </Stack>
                  {row.refreshed && (
                    <>
                      <Typography color="text.secondary">
                        last refreshed
                      </Typography>
                      <Typography>
                        <RelativeDate
                          date={DateTime.fromJSDate(row.refreshed)}
                        />
                      </Typography>
                    </>
                  )}
                </Stack>
                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={{ md: 1, xs: 0 }}
                >
                  <FormatUserAgent agent={row.userAgent} />
                  {row.sharedDevice ? (
                    <Chip color="warning" label="Shared Device" size="small" />
                  ) : undefined}
                </Stack>
              </Stack>
            </TableCell>
            <TableCell sx={{ textAlign: 'end' }}>
              {row.revoked ? (
                <Chip
                  label="Revoked"
                  size="small"
                  sx={{ backgroundColor: 'red' }}
                />
              ) : row.expires < new Date() ? (
                <Chip
                  label="Expired"
                  size="small"
                  sx={{ backgroundColor: 'orange' }}
                />
              ) : (
                <ConfirmButton busy={revoking} onClick={() => revoke(row)}>
                  Revoke
                </ConfirmButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
